import { useRef } from 'react';
import CardOverlay, { CardFace } from './CardOverlay';
import { CostLabel } from './Amount';
import { AggressionPicker } from './AggressionPicker';
import { PactPicker } from './PactPicker';
import { useHover } from '../utils/Mobile';
const { Modal } = require('react-bootstrap');
const { HimejiUnits } = require('@tta/engine/Moves/Aggression');

// Something (a button) that shows a card while hovered, e.g. the action panel's button that plays an action card
export function HoverCard({Card, children})
{
    const [shown, setShown, hoverProps] = useHover();
    const ref = useRef(null);
    if (!Card)
    {
        return children;
    }
    return (
        <>
            <span ref={ref} style={{ display: 'inline-block' }} {...hoverProps}>{children}</span>
            <CardOverlay ShowOverlay={shown} ComponentRef={ref} card={Card} callback={() => {}} OnClose={() => setShown(false)}/>
        </>
    );
}

// Asked before every event is prepared (from the action panel, or picked with the action bar): the card in full, and
// the culture preparing it scores
export function PrepareConfirm({Card, Option, OnConfirm, OnCancel})
{
    return (
        <Modal show onHide={OnCancel} centered dialogClassName="tta-prepare-dialog">
            <Modal.Header closeButton style={{ padding: '8px 12px' }}>
                <Modal.Title style={{ fontSize: '16px', fontWeight: 'bold' }}>Prepare this event?</Modal.Title>
            </Modal.Header>
            <Modal.Body className="tta tta-prepare-body">
                {Card && <CardFace card={Card}/>}
                {Option.cost && <div className="tta-prepare-gain">Preparing it scores <CostLabel cost={Option.cost}/></div>}
            </Modal.Body>
            <Modal.Footer className="tta" style={{ padding: '6px 12px' }}>
                <button className="tta-button" onClick={OnCancel}>Cancel</button>
                <button className="tta-button dark" onClick={() => OnConfirm(Option)}>Prepare</button>
            </Modal.Footer>
        </Modal>
    );
}

// The bar's question once a political card is picked (see the engine's ActionBar.js, ask): an event confirmed before it
// is prepared, or the rival chosen for an aggression, a war or a pact. Its options are the card's; one chosen is done,
// cancelling picks again (or, from a click in hand, switches the trigger off).
export function AskedChoice({Ask, Choice, GameEngine})
{
    const player = GameEngine.gameState.GetCurrentPlayerState();
    const cardId = Choice.options[0]?.move.cardId;
    const card = player.militaryHand.find(c => c?.id === cardId);
    const choose = (option) => GameEngine.ChooseTarget(option);
    const cancel = () => GameEngine.ChooseTarget(null);
    if (Ask === 'prepare')
    {
        return <PrepareConfirm Card={card} Option={Choice.options.find(option => !option.disabled) ?? Choice.options[0]} OnConfirm={choose} OnCancel={cancel}/>;
    }
    if (Ask === 'pact')
    {
        return <PactPicker Card={card} Label={card?.name} Options={Choice.options} Targets={GameEngine.GetPactTargets(cardId)}
            ButtonClass="tta-button political" OnChoose={choose} OnCancel={cancel}/>;
    }
    const war = Ask === 'war';
    return <AggressionPicker Card={card} Label={card?.name} Options={Choice.options} War={war}
        Units={war ? [] : HimejiUnits(player)} Strength={player.GetMilitaryStrength()}
        Targets={war ? GameEngine.GetWarTargets(cardId) : GameEngine.GetAggressionTargets(cardId)}
        ButtonClass="tta-button political" OnChoose={choose} OnCancel={cancel}/>;
}

// A card in hand that can be used more than one way, clicked (Confucius: an aggression, a war or a pact can be
// prepared too): the card, and a button for each way; one chosen goes on as that trigger would
export function HandWays({Ways, GameEngine})
{
    const card = GameEngine.gameState.GetCurrentPlayerState().militaryHand.find(c => c?.id === Ways.cardId);
    return (
        <Modal show onHide={() => GameEngine.ChooseTarget(null)} centered dialogClassName="tta-prepare-dialog">
            <Modal.Header closeButton style={{ padding: '8px 12px' }}>
                <Modal.Title style={{ fontSize: '16px', fontWeight: 'bold' }}>{card?.name ?? 'This card'}: what will you do?</Modal.Title>
            </Modal.Header>
            <Modal.Body className="tta tta-prepare-body">
                {card && <CardFace card={card}/>}
                {Ways.ways.map(way =>
                    <button key={way.trigger} className="tta-button political" style={{ width: '100%' }}
                        onClick={() => GameEngine.ClickHandCard(Ways.key, way.trigger)}>{way.label}</button>)}
            </Modal.Body>
            <Modal.Footer className="tta" style={{ padding: '6px 12px' }}>
                <button className="tta-button" onClick={() => GameEngine.ChooseTarget(null)}>Cancel</button>
            </Modal.Footer>
        </Modal>
    );
}
